/**
 * Referral lookup / register (TMMAPP-0015) — `GET/POST /v1/me/referral`.
 */
import type { ReferralLookupView as WireLookup, ReferralRegisterRequest } from '@/api/generated';
import { request } from './client';

export type ReferralState =
  'CAN_REGISTER' | 'ALREADY_THIS' | 'CAN_SWITCH' | 'LOCKED' | 'NO_SUBSCRIPTION';

const STATES: ReadonlySet<string> = new Set([
  'CAN_REGISTER',
  'ALREADY_THIS',
  'CAN_SWITCH',
  'LOCKED',
  'NO_SUBSCRIPTION',
]);

export interface ReferralCurrent {
  affiliateDisplayName: string;
  attributedAt: string;
}

export interface ReferralLookup {
  code: string;
  affiliateDisplayName: string;
  /** `null` for a state this app does not know. */
  state: ReferralState | null;
  current: ReferralCurrent | null;
  /** The end of the switch window (an instant) while CAN_REGISTER or CAN_SWITCH (TMMAPI-0169). */
  changeableUntil: string | null;
}

export function normalizeReferralLookup(w: WireLookup): ReferralLookup {
  return {
    code: w.code,
    affiliateDisplayName: w.affiliateDisplayName,
    state: STATES.has(w.state) ? w.state : null,
    current: !w.current
      ? null
      : {
          affiliateDisplayName: w.current.affiliateDisplayName,
          attributedAt: w.current.attributedAt,
        },
    changeableUntil: w.changeableUntil ?? null,
  };
}

export const referralApi = {
  async lookup(code: string): Promise<ReferralLookup> {
    return normalizeReferralLookup(
      await request<WireLookup>('GET', '/v1/me/referral', { query: { code } }),
    );
  },
  /** POST /v1/me/referral — sends `confirm` only when given. */
  async register(body: ReferralRegisterRequest): Promise<ReferralLookup> {
    return normalizeReferralLookup(
      await request<WireLookup>('POST', '/v1/me/referral', { json: body }),
    );
  },
};
